import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Shell } from "./components/Shell";
import { Unavailable } from "./components/ui";
import { configured, supabase } from "./lib/supabase";
import { loadRpc } from "./lib/platform";
import { Clients } from "./pages/Clients";
import { Dashboard } from "./pages/Dashboard";
import { Audit, EmailOps, Kiosks, Sites, Staff, Visits } from "./pages/Lists";
import { Login, LoginStatus } from "./pages/Login";

function AppRoutes() {
  const [session, setSession] = useState(undefined);
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase) { setSession(null); return undefined; }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_e, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user) { setMe(null); return; }
    loadRpc("platform_me").then(setMe).catch((err) => setError(err.message));
  }, [session]);

  async function signIn(email, password) {
    setBusy(true);
    setNotice("");
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    setError(signInError?.message || "");
  }

  async function resetPassword(email) {
    setBusy(true);
    setError("");
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/` });
    setBusy(false);
    if (resetError) setError(resetError.message);
    else setNotice("If the account exists, a password reset email is on its way.");
  }

  if (session === undefined) return <LoginStatus title="Loading session" />;
  if (!configured) return <LoginStatus title="Platform sign in" detail="Supabase environment variables are missing." />;
  if (!session) return <Login onSubmit={signIn} onReset={resetPassword} onClear={() => { setError(""); setNotice(""); }} error={error} notice={notice} busy={busy} />;
  if (error && !me) return <LoginStatus title="This account is not a platform administrator" detail={error} action={<button type="button" className="rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white" onClick={() => supabase.auth.signOut()}>Sign out</button>} />;
  if (!me) return <LoginStatus title="Loading platform profile" />;

  return (
    <Shell me={me} onSignOut={() => supabase.auth.signOut()}>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="sites" element={<Sites />} />
        <Route path="visits" element={<Visits />} />
        <Route path="kiosks" element={<Kiosks />} />
        <Route path="email" element={<EmailOps />} />
        <Route path="audit" element={<Audit />} />
        <Route path="staff" element={<Staff />} />
        <Route path="contractors" element={<Unavailable icon="staff" title="Contractors" reason="Contractor companies exist on visits. Document expiry, induction, and access credentials are not separate platform modules." />} />
        <Route path="access" element={<Unavailable icon="lock" title="Access control" reason="Genetec, LenelS2, Gallagher, Suprema, and HikCentral are a roadmap. No live adapter is connected." />} />
        <Route path="health" element={<Unavailable icon="kiosks" title="System health" reason="There is no telemetry pipeline. Kiosk last-seen is on the Kiosks page. Email failures are on the Email page." />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() { return <AppRoutes />; }
